import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Card from "components/card";
import { Form, Select, Input } from "antd";
import FormItem from "components/fields/FormItem";
import { requiredField } from "utils/crudSagaHelper";
import TextArea from "antd/es/input/TextArea";
import CrudButtons from "components/button/crudbuttons";
import { Send } from "lucide-react";
import {
    getTicketDetails,
    doTicketCreate,
} from "../../../../../redux/ticket/ticketState";

const SupportTicket = ({
    onCancel,
    getRecord,
    recordData, 
    requesting, 
    success,
    doCreate, 
    doUpdate, 
    variant = 'it-support',
    ...props
}) => {
    const { recordId, mode } = props;
    const [form] = Form.useForm();
    const [selectedTitleId, setSelectedTitleId] = useState(null);
    const [otherTitleValue, setOtherTitleValue] = useState("");

    const config = {
        "it-support": {
            titlesField: "ITtitles",
            categoryField: "ITcategory",
            priorityField: "priority",
            submitButtonClass: "blue",
            placeholder: "Brief description of the issue",
        },
        "hr-support": {
            titlesField: "HRtitles",
            categoryField: "HRcategory",
            priorityField: "priority",
            submitButtonClass: "rose",
            placeholder: "Brief description of your request",
        },
        "partner-service": {
            titlesField: "PStitles",
            categoryField: "PScategory",
            priorityField: "priority",
            submitButtonClass: "purple",
            placeholder: "Brief description of your inquiry",
        },
    };

    const currentConfig = config[variant] || config['it-support'];

    useEffect(() => {
        if(recordId){
            getRecord(recordId);
        }
    }, [recordId, getRecord]);

    useEffect(() => {
        if (recordData) {
            form.resetFields();
            if (recordData.tic_title_id === currentConfig.othersTitleId && recordData.tic_title) {
                setSelectedTitleId(currentConfig.othersTitleId);
                setOtherTitleValue(recordData.tic_title);
                form.setFieldsValue({ 
                    tic_title_id: currentConfig.othersTitleId,
                    tic_custom_title: recordData.tic_title 
                });
            } else if (recordData.tic_title_id) {
                setSelectedTitleId(recordData.tic_title_id);
                form.setFieldsValue({ tic_title_id: recordData.tic_title_id });
            }
            
            form.setFieldsValue({
                tic_category_id: recordData.tic_category_id,
                tic_priority_id: recordData.tic_priority_id,
                tic_description: recordData.tic_description
            });
        }
    }, [recordData, form, currentConfig]);

    const categoryOptions = recordData?.[currentConfig.categoryField] || [];
    const priorityOptions = recordData?.[currentConfig.priorityField] || [];
    const titleOptions = recordData?.[currentConfig.titlesField] || [];

    const othersTitleId = titleOptions.find(
        option => option.is_other
    )?.value;

    const showOtherInput = selectedTitleId === othersTitleId;


    const onSubmit = (formData) => {
        try {
            const selectedCategory = categoryOptions.find(
                item => item.value === Number(formData.tic_category_id)
            );

            formData.tic_department_id = selectedCategory?.department_id ?? null;
            formData.tic_queue_id = selectedCategory?.queue_id ?? null;

            if (selectedTitleId === othersTitleId && otherTitleValue.trim()) {
                formData.tic_title_id = othersTitleId;
                formData.tic_title = otherTitleValue.trim();
            } else {
                formData.tic_title_id = selectedTitleId;
                formData.tic_title = null;
            }

            delete formData.tic_custom_title;

            recordData?.tic_id > 0
                ? doUpdate(formData)
                : doCreate(formData);

        } catch (error) {
            console.error('Save failed:', error);
        }
    };

    const handleTitleChange = (value) => {
        setSelectedTitleId(value);
        if (value !== currentConfig.othersTitleId) {
            setOtherTitleValue("");
            form.setFieldsValue({ tic_custom_title: undefined });
        }
    };

    return (
        <Card className="p-0">
            <Form
                form={form}
                className={mode !== "view" ? "form" : ""}
                layout="vertical" 
                onFinish={onSubmit}
            >
                <div>
                    <div className="space-y-2">
                        <FormItem
                            name="tic_title_id" 
                            label="Issue Title"
                            rules={[requiredField()]}
                        >
                            <Select 
                                id="tic_title_id"
                                allowClear={true}
                                className="input-field"
                                options={titleOptions}
                                placeholder={currentConfig.placeholder}
                                onChange={handleTitleChange}
                                value={selectedTitleId}
                            />
                        </FormItem>
                        
                        {showOtherInput && (
                            <FormItem
                                name="tic_custom_title"
                                label="Please specify your issue title"
                                rules={[requiredField()]}
                            >
                                <Input
                                    id="tic_custom_title"
                                    className="input-field"
                                    placeholder="Enter your issue title..."
                                    value={otherTitleValue}
                                    onChange={(e) => setOtherTitleValue(e.target.value)}
                                />
                            </FormItem>
                        )}
                        
                        <FormItem
                            name="tic_category_id" 
                            label="Category"
                            rules={[requiredField()]}
                        >
                            <Select 
                                id="tic_category_id"
                                allowClear={true}
                                className="input-field"
                                options={categoryOptions}
                                placeholder="Select category"
                            />
                        </FormItem>
                        
                        <FormItem
                            name="tic_priority_id" 
                            label="Priority"
                            rules={[requiredField()]}
                        >
                            <Select 
                                id="tic_priority_id"
                                allowClear={true}
                                className="input-field"
                                options={priorityOptions}
                                placeholder="Select priority"
                            />
                        </FormItem>
                        
                        <FormItem
                            name="tic_description"
                            label="Details"
                            rules={[requiredField()]}
                        >
                            <TextArea
                                id="tic_description" 
                                className="input-field"
                                maxLength={500}
                                placeholder="Provide details about your issue..."
                                rows={3}
                            />
                        </FormItem>
                    </div>
                    <div className="flex gap-2 pt-2 flex-row-reverse">
                        <CrudButtons
                            mode={mode}
                            recordId={recordData?.tic_id ?? 0}
                            isModal={true}
                            modalId={recordData?.tic_id > 0 ? "edit_ticket" : "add_ticket"}
                            doHideModal={onCancel}
                            cancelLabel="Cancel"
                            submitLabel="Submit Ticket"
                            moduleId={0}
                            cancelIcon=""
                            submitIcon={<Send className="w-4 h-4 mr-2" />}
                            submitClassName={`ticket submit-ticket ${currentConfig.submitButtonClass}`}
                            cancelClassName="ticket cancel-ticket"
                        />
                    </div>
                </div>
            </Form>
        </Card>
    )
};

const mapStateToProps = (state) => ({
    recordData: state.ticket.ticketDetails?.data,
    requesting: state.ticket.ticketDetails?.requesting,
    success: state.ticket.ticketDetails?.succcess,
});

const mapDispatchToProps = (dispatch) => ({
    getRecord: (id) => dispatch(getTicketDetails({ tic_id: id })),
    doCreate: (data) => dispatch(doTicketCreate(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupportTicket);